import { Action, createAction, handleActions } from 'redux-actions'
import { call, cancel, put, select, takeLatest } from 'redux-saga/effects'
import { oc } from 'ts-optchain'

import { fetchBill } from '@/api/BillPaymentApi'
import * as Bridge from '@/api/BridgeApi'
import { LEAVE_ALERT_TYPE } from '@/components/common/layer/Leave/LeaveLayer'
import { EmptySimpleAccount, ISimpleAccountResponse } from '@/domains/AccountDomain'
import {
  EmptyBill,
  IBillPaymentInquireRequest,
  IBillPaymentInquireResponse,
  PAYMENT_TYPE_CODE,
} from '@/domains/BillPaymentDomain'
import { STATUS_CODE } from '@/domains/CommonDomain'
import { IRootState } from '@/modules'
import { simpleAccountSelector } from '@/modules/account/SimpleAccountModule'
import { billPaymentMainSelector } from '@/modules/bill-payment/BillPaymentMainModule'
import { errorActions } from '@/modules/common/error'
import { layerActions } from '@/modules/common/layer'
import { loadingActions } from '@/modules/common/loading'
import { createPostAction } from '@/utils/actionUtils'
import { BRIDGE_ERROR_CODE } from '@/utils/bridgeUtils/constants'
import { IAccountInfoResult } from '@/utils/bridgeUtils/features/getMyAccount'
import { IWithdrawalAccountParams } from '@/utils/bridgeUtils/features/getWithdrawalAccount'
import { toInteger } from '@/utils/numberUtils'
import { apiSuccessReducer, reset } from '@/utils/reducerUtils'
import { isEmpty, isZero } from '@/utils/validationUtils'

export const BILL_PAYMENT_AMOUNT = 'BillPaymentAmount'

export const VALIDATE_BILL = `${BILL_PAYMENT_AMOUNT}/validateBill`

const SET_CUSTOMER_ID = `${BILL_PAYMENT_AMOUNT}/setCustomerId`
const SET_ACCOUNT_BY_ACCT_NO = `${BILL_PAYMENT_AMOUNT}/selectedAccountByAcctNo`
const SET_ACCOUNT = `${BILL_PAYMENT_AMOUNT}/selectedAccount`
const SET_AMOUNT = `${BILL_PAYMENT_AMOUNT}/amount`
const SET_WITHDRAWAL_ACCOUNT = `${BILL_PAYMENT_AMOUNT}/setWithdrawalAccount`
const SET_VIEW = `${BILL_PAYMENT_AMOUNT}/setView`
const CLEAR_STATE = `${BILL_PAYMENT_AMOUNT}/clearState`

export interface IBillPaymentAmountView {
  expandDetailInformation: boolean
}

export interface IBillPaymentAmountState extends IBillPaymentInquireResponse {
  selectedAccount: ISimpleAccountResponse | null
  view: IBillPaymentAmountView
}

const initialState: IBillPaymentAmountState = {
  ...EmptyBill,
  selectedAccount: null,
  view: {
    expandDetailInformation: false,
  },
}

export const billPaymentAmountActions = {
  setCustomerId: createAction<string>(SET_CUSTOMER_ID),
  setAmount: createAction<string>(SET_AMOUNT),
  setAccountByAcctNo: createAction<string>(SET_ACCOUNT_BY_ACCT_NO),
  setAccount: createAction<Pick<IBillPaymentAmountState, 'selectedAccount'>>(SET_ACCOUNT),
  setWithdrawalAccount: createAction(SET_WITHDRAWAL_ACCOUNT),
  setView: createAction<Partial<IBillPaymentAmountView>>(SET_VIEW),
  clearState: createAction(CLEAR_STATE),
}

export const validateBillAsync = createPostAction<IBillPaymentInquireRequest>(VALIDATE_BILL)

const reducer = {
  [validateBillAsync.SUCCESS]: apiSuccessReducer,
  [SET_CUSTOMER_ID]: (state: IBillPaymentAmountState, action: Action<any>) => ({
    ...state,
    customerId: action.payload,
  }),
  [SET_AMOUNT]: (state: IBillPaymentAmountState, action: Action<string>) => ({
    ...state,
    amount: action.payload,
  }),
  [SET_ACCOUNT]: apiSuccessReducer,
  [SET_VIEW]: (state: IBillPaymentAmountState, action: Action<any>) => ({
    ...state,
    view: {
      ...state.view,
      ...action.payload,
    },
  }),
  [CLEAR_STATE]: (state: any) => reset(state, initialState),
}

export const billPaymentAmountReducer = handleActions(reducer, initialState)

export const billPaymentAmountSelector = {
  view: (state: IRootState): IBillPaymentAmountView =>
    state[BILL_PAYMENT_AMOUNT].view ?? initialState.view,
  isExpandDetailInformation: (state: IRootState, productTypeCode: string): boolean => {
    const { expandDetailInformation }: IBillPaymentAmountView = billPaymentAmountSelector.view(
      state,
    )
    const { paymentTypeCode } = billPaymentMainSelector.flattenProductType(state, productTypeCode)
    const isPulsa = paymentTypeCode === PAYMENT_TYPE_CODE.PULSA

    return isPulsa || (!isPulsa && expandDetailInformation)
  },
  selectedAccount: (state: IRootState) => {
    const majorAccount = simpleAccountSelector.majorAccount(state)

    return oc(state[BILL_PAYMENT_AMOUNT]).selectedAccount(majorAccount) || EmptySimpleAccount
  },
  isTransferLimitOver: (state: IRootState) => {
    const { baseCurJan } = billPaymentAmountSelector.selectedAccount(state)
    const { amount } = state[BILL_PAYMENT_AMOUNT]

    return toInteger(amount) > toInteger(baseCurJan)
  },
  isValidInput: (state: IRootState, productTypeCode: string): boolean => {
    const { productTypeSwitchId } = billPaymentMainSelector.flattenProductType(
      state,
      productTypeCode,
    )
    const { acctNo } = billPaymentAmountSelector.selectedAccount(state)
    const { customerId, reserved } = state[BILL_PAYMENT_AMOUNT]
    const isValidAmount = billPaymentAmountSelector.isValidAmount(state)
    const isValidAcctNo = !isEmpty(acctNo) && !isZero(acctNo)
    const isValidCustomerId = !isEmpty(customerId) && !isZero(customerId)
    const isValidProductTypeCode = !isEmpty(productTypeCode)
    const isValidProductTypeSwitchId = !isEmpty(productTypeSwitchId)
    const isValidReserved = !isEmpty(reserved)

    return (
      isValidAmount &&
      isValidAcctNo &&
      isValidCustomerId &&
      isValidProductTypeCode &&
      isValidProductTypeSwitchId &&
      isValidReserved
    )
  },
  isValidAmount: (state: IRootState): boolean => {
    const { amount } = state[BILL_PAYMENT_AMOUNT]
    const isTransferLimitOver = billPaymentAmountSelector.isTransferLimitOver(state)

    if (toInteger(amount) <= 0) {
      return false
    }

    return !isTransferLimitOver
  },
}

export function* validateBill(action: Action<IBillPaymentInquireRequest>) {
  try {
    yield put(loadingActions.start(VALIDATE_BILL))
    const response: IBillPaymentInquireResponse = yield call(fetchBill, action.payload)
    yield put(validateBillAsync.success(response))
  } catch (e) {
    if (e.code === STATUS_CODE.BILL_PAYMENT_TRANSACTION_FAIL) {
      yield put(layerActions.open(LEAVE_ALERT_TYPE.BILL_PAYMENT_TRANSACTION_FAIL))
    } else {
      yield put(errorActions.set(e))
    }
  } finally {
    yield put(loadingActions.finish(VALIDATE_BILL))
  }
}

export function* setWithdrawalAccount() {
  const { acctNo: selectedAccountNo }: ISimpleAccountResponse = yield select(
    billPaymentAmountSelector.selectedAccount,
  )
  const {
    [BILL_PAYMENT_AMOUNT]: { amount },
  }: IRootState = yield select()

  const param: IWithdrawalAccountParams = {
    title: 'Withdraw Account',
    selectedAccountNo,
    amount,
  }

  try {
    const { result: selectedAccount }: IAccountInfoResult = yield call(
      Bridge.getWithdrawalAccount,
      param,
    )

    if (selectedAccount) {
      yield put(
        billPaymentAmountActions.setAccount({
          selectedAccount,
        }),
      )
    }
  } catch (e) {
    if (e.code === BRIDGE_ERROR_CODE.CLOSE) {
      yield cancel()
    }

    yield put(errorActions.set(e))
  }
}

export function* setAccountByAcctNo(action: Action<string>) {
  const acctNo = action.payload
  const accounts: ISimpleAccountResponse[] = yield select(simpleAccountSelector.simpleAccounts)
  const selectedAccount = accounts.find(account => account.acctNo === acctNo) || EmptySimpleAccount

  yield put(
    billPaymentAmountActions.setAccount({
      selectedAccount,
    }),
  )
}

export const billPaymentAmountSaga = [
  takeLatest(validateBillAsync.POST, validateBill),
  takeLatest(SET_WITHDRAWAL_ACCOUNT, setWithdrawalAccount),
  takeLatest(SET_ACCOUNT_BY_ACCT_NO, setAccountByAcctNo),
]
